import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { FALLBACK_ERROR_MESSAGE } from "@/lib/constants";
import { deleteDeploy } from "@/lib/deploy/api"; // <-- make sure you have this API
import { getDeployStatusColor } from "@/lib/deploy/utils";
import { humanizeString } from "@/lib/utils";
import { Deploy } from "@/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Trash2 } from "lucide-react";
import { useState } from "react";

interface DeleteConfirmDialogProps {
  deploy: Deploy;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DeleteConfirmDialog({
  deploy,
  open,
  onOpenChange,
}: DeleteConfirmDialogProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const deleteDeployMutation = useMutation({
    mutationFn: deleteDeploy,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deploys"] });
      queryClient.invalidateQueries({ queryKey: ["deploy", deploy.id] });
      toast({
        title: "Application queued for deletion",
        description: `${deploy.repoName} has been queued for deletion.`,
      });
      onOpenChange(false);
    },
    onError: (error: any) => {
      setIsDeleting(false);

      toast({
        title: "Error",
        description: error?.response?.data?.message ?? FALLBACK_ERROR_MESSAGE,
        variant: "destructive",
      });
    },
  });

  const handleDelete = () => {
    setIsDeleting(true);
    deleteDeployMutation.mutate(deploy.id);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-slate-900 border-red-500/30">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-red-400">
            <Trash2 className="h-5 w-5" />
            DELETE APPLICATION
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Current Deploy Info */}
          <Card className="holo-card">
            <CardContent>
              <div className="space-y-4 mt-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-cyan-100">
                    {deploy.repoName}
                  </h4>
                  <Badge className={`${getDeployStatusColor(deploy.status)} `}>
                    {humanizeString(deploy.status)}
                  </Badge>
                </div>

                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-slate-400">Last deployed:</span>
                    <p className="text-cyan-100">
                      {new Date(deploy.updatedAt).toLocaleDateString()} at{" "}
                      {new Date(deploy.updatedAt).toLocaleTimeString()}
                    </p>
                  </div>
                  <div>
                    <span className="text-slate-400">Environment:</span>
                    <p className="text-cyan-100">
                      {humanizeString(deploy.env)}
                    </p>
                  </div>
                  <div>
                    <span className="text-slate-400">Branch:</span>
                    <p className="text-cyan-100">{deploy.branch}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Permanent Deletion Warning */}
          <Card className="holo-card border-red-500/30">
            <CardContent className="flex gap-3 items-start">
              <AlertTriangle className="h-5 w-5 text-red-400 mt-0.5" />
              <p className="text-red-100 text-sm">
                This action <strong>cannot be undone</strong>. Deleting this
                application will remove all deployments, logs, and associated
                data permanently.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4 pt-4 border-t border-slate-700/50">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={isDeleting}
            variant="destructive"
          >
            <Trash2
              className={`h-4 w-4 mr-2 ${isDeleting ? "animate-spin" : ""}`}
            />
            {isDeleting ? "DELETING..." : "DELETE"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
